import { Briefcase, Building2, Clock, FlaskConical, LogOut, Megaphone, PanelRightClose, PanelRightOpen } from "lucide-react";
import React, { useMemo } from "react";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/shared/components/ui/sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";

import { BrandLogo } from "@/shared/components/BrandLogo";
import { Button } from "@/shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { useSettingsCache } from "@/shared/cache/slices/settings";
import { ProfileMenu, type ProfileMenuItem } from "@/shared/components/ProfileMenu/ProfileMenu";
import { useUpdateNotification } from "@/shared/hooks/useUpdateNotification";
import { WorkspaceSwitcher } from "@/shared/components/WorkspaceSwitcher/WorkspaceSwitcher";
import { hasAnyAdminAccess } from "@/shared/config/adminPermissions";

export type InternalView = "entries" | "sandbox" | "updates";

const navItems: Array<{
	view: InternalView;
	label: string;
	icon: React.ElementType;
}> = [
	{ view: "entries", label: "Entries", icon: Clock },
	{ view: "sandbox", label: "Sandbox", icon: FlaskConical },
];

const InternalSidebar: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, signOut } = useAuth();
	const { settings } = useSettingsCache();
	const { hasNewUpdate, dismissUpdate } = useUpdateNotification();
	const { state, toggleSidebar } = useSidebar();
	const collapsed = state === "collapsed";

	const showAdminEscape = hasAnyAdminAccess(user?.email);

	const currentView: InternalView = useMemo(() => {
		const segments = location.pathname.split("/").filter(Boolean);
		const segment = segments[1] || "entries";
		switch (segment) {
			case "entries": return "entries";
			case "calendar": return "entries"; // Calendar is a tab within Entries
			case "sandbox": return "sandbox";
			case "updates": return "updates";
			default: return "entries";
		}
	}, [location.pathname]);

	const userInitials = useMemo(() => {
		if (user?.firstName && user?.lastName) {
			return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
		}
		if (user?.email) {
			return user.email[0].toUpperCase();
		}
		return "?";
	}, [user]);

	const userDisplayName = useMemo(() => {
		if (user?.firstName && user?.lastName) {
			return `${user.firstName} ${user.lastName}`;
		}
		return user?.email ?? "";
	}, [user]);

	const profileMenuItems: ProfileMenuItem[] = [
		{ label: "Updates", icon: Megaphone, isActive: currentView === "updates", onClick: () => { dismissUpdate(); navigate("/internal/updates"); }, separator: true, badge: hasNewUpdate },
		{ label: "Sign out", icon: LogOut, onClick: () => signOut({ returnTo: window.location.origin }), separator: true },
	];

	return (
		<Sidebar
			collapsible="icon"
			className="bg-sidebar border-r border-sidebar-border [&_[data-sidebar=sidebar]]:bg-sidebar"
		>
			<SidebarHeader className="px-4 pt-7 pb-2 text-sidebar-foreground group-data-[collapsible=icon]:px-0">
				<div className="flex h-7 items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
					<BrandLogo
						className="h-7 w-auto group-data-[collapsible=icon]:hidden"
						aria-label="Hourglass"
					/>
					<Button
						variant="ghost"
						size="icon"
						onClick={toggleSidebar}
						aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
						className="mb-1 size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground [&_svg]:size-5"
					>
						{collapsed ? <PanelRightClose /> : <PanelRightOpen />}
					</Button>
				</div>
				{collapsed && (
					<>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									onClick={() => navigate("/entries")}
									aria-label="Switch to Timekeeper view"
									className="mx-auto mt-3 hidden size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:flex [&_svg]:size-5"
								>
									<Briefcase />
								</Button>
							</TooltipTrigger>
							<TooltipContent side="right">Switch to Timekeeper</TooltipContent>
						</Tooltip>
						{showAdminEscape && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => navigate("/admin")}
										aria-label="Switch to Admin view"
										className="mx-auto mt-2 hidden size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:flex [&_svg]:size-5"
									>
										<Building2 />
									</Button>
								</TooltipTrigger>
								<TooltipContent side="right">Switch to Admin</TooltipContent>
							</Tooltip>
						)}
					</>
				)}
				<WorkspaceSwitcher current="internal" />
			</SidebarHeader>

			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{navItems.map((item) => (
								<SidebarMenuItem key={item.view}>
									<SidebarMenuButton
										isActive={currentView === item.view}
										tooltip={item.label}
										onClick={() => navigate(`/internal/${item.view}`)}
										className="h-10 px-3 gap-3 data-[active=true]:font-normal [&>svg]:size-5"
									>
										<item.icon />
										<span>{item.label}</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>

			<SidebarFooter>
				<ProfileMenu
					userDisplayName={userDisplayName}
					userInitials={userInitials}
					userEmail={user?.firstName ? user.email : undefined}
					subtitle={settings?.prof_type || undefined}
					menuItems={profileMenuItems}
				/>
			</SidebarFooter>
		</Sidebar>
	);
};

export default InternalSidebar;
